import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Edit2, Eye, CheckCircle, XCircle } from 'lucide-react';
import { Layout } from '../components/Layout';
import { PlanBuilder } from '../components/PlanBuilder';
import { planApi } from '../services/api';

export function Plans() {
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | undefined>();

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await planApi.list();
      return response.data;
    },
  });

  const handleCreateNew = () => {
    setEditingPlanId(undefined);
    setShowBuilder(true);
  };

  const handleEdit = (planId: string) => {
    setEditingPlanId(planId);
    setShowBuilder(true);
  };

  const handleClose = () => {
    setShowBuilder(false);
    setEditingPlanId(undefined);
  };

  const tierColors = {
    free: 'bg-gray-100 text-gray-800',
    standard: 'bg-blue-100 text-blue-800',
    premium: 'bg-purple-100 text-purple-800',
  };

  const difficultyColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800',
  };

  if (showBuilder) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <button
              onClick={handleClose}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              ← Back to Plans
            </button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            {editingPlanId ? 'Edit Training Plan' : 'Create Training Plan'}
          </h1>
          <PlanBuilder planId={editingPlanId} onCancel={handleClose} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Training Plans</h1>
            <p className="mt-2 text-gray-600">
              Create and manage your training plans here.
            </p>
          </div>
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-5 h-5" />
            Create Plan
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading plans...</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500 mb-4">No training plans yet</p>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-5 h-5" />
              Create Your First Plan
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-gray-200 flex items-center justify-center">
                  {plan.thumbnail_url ? (
                    <img
                      src={plan.thumbnail_url}
                      alt={plan.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Eye className="w-12 h-12 text-gray-400" />
                  )}
                  <div className="absolute top-2 right-2">
                    {plan.is_published ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-600 text-white">
                        <CheckCircle className="w-3 h-3" />
                        Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-600 text-white">
                        <XCircle className="w-3 h-3" />
                        Draft
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 truncate mb-2">{plan.title}</h3>
                  {plan.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{plan.description}</p>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${tierColors[plan.tier]}`}>
                      {plan.tier}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${difficultyColors[plan.difficulty]}`}>
                      {plan.difficulty}
                    </span>
                    {plan.duration_weeks && (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                        {plan.duration_weeks} weeks
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(plan.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
